/**
 * useRunSteps — React Query hook for environment run stage-level steps.
 *
 * Polls while the parent run is still active (3s interval).
 * Disables polling once the run reaches a terminal status.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchRunSteps } from '../api/environmentRuns';
import type { RunStep, EnvironmentRunStatus } from '../types/environmentRun';
import { ENV_TERMINAL_STATUSES } from '../types/environmentRun';

const EMPTY_STEPS: RunStep[] = [];

export function useRunSteps(
  runId: string | null,
  runStatus: EnvironmentRunStatus | null,
) {
  const isTerminal = runStatus ? ENV_TERMINAL_STATUSES.includes(runStatus) : false;

  const query = useQuery({
    queryKey: ['environmentRunSteps', runId],
    queryFn: () => fetchRunSteps(runId!),
    enabled: !!runId,
    staleTime: 3000,
    refetchInterval: isTerminal ? false : 3000,
  });

  return {
    steps: query.data ?? EMPTY_STEPS,
    isLoading: query.isLoading,
    error: query.error,
  };
}
